import { isCampaignBadge, creativeTokenFromId } from '../../../../shared/report-identity.ts';
import { SocialBadge, GeoBadges } from '../../core/components/AssetBadges.tsx';
import { usePersistentState } from '../../core/usePersistentState.ts';
import { quoteBudget } from '../../../../shared/budget.ts';
// modules/ads-stats/AdsStatsPage.tsx
// ДОМЕН 5 — Звіт «По оголошеннях» з сортованою таблицею

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAdsStats, getPresets, type SortField, type DateRange, type TabKey, type AdsReportRow } from './useAdsStats.ts';
import { useSelectableTotal } from './useSelectableTotal.ts';
import { calculateReportTotals } from './report-totals.ts';
import { AccountFilterModal } from './AccountFilterModal.tsx';
import { CopyForSheetsModal } from './CopyForSheetsModal.tsx';
import { exportAdsReportToXlsx } from '../../core/xlsxExport.ts';
import { api, ApiClientError } from '../../core/apiClient.ts';
import { TriggerFeature } from '../triggers/TriggerFeature.tsx';
import { TriggerBadge, automationStyle } from '../triggers/TriggerBadge.tsx';
import { useToast } from '../../core/contexts/ToastContext.tsx';

// ─── Column visibility ─────────────────────────────────────────

type ColKey =
  | 'ad_name' | 'ad_status' | 'campaign_name' | 'ad_account_id'
  | 'spend' | 'impressions' | 'clicks' | 'cpm' | 'cpc' | 'ctr'
  | 'leads' | 'cost_per_lead'
  | 'registrations' | 'cost_per_registration'
  | 'purchases' | 'cost_per_purchase'
  | 'app_installs' | 'cost_per_app_install'
  | 'budget';

const DEFAULT_VISIBLE_PER_TAB: Record<TabKey, ColKey[]> = {
  ads: [
    'ad_name', 'ad_status', 'ad_account_id',
    'spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr',
    'leads', 'cost_per_lead',
    'registrations', 'cost_per_registration',
    'purchases', 'cost_per_purchase', 'budget'
  ],
  campaigns: [
    'campaign_name', 'ad_status', 'ad_account_id',
    'spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr',
    'leads', 'cost_per_lead',
    'registrations', 'cost_per_registration',
    'purchases', 'cost_per_purchase', 'budget'
  ],
  geo: [
    'ad_name', 'ad_status', 'ad_account_id',
    'spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr',
    'leads', 'cost_per_lead',
    'registrations', 'cost_per_registration',
    'purchases', 'cost_per_purchase',
  ],
  accounts: [
    'ad_name', 'ad_status', 'ad_account_id',
    'spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr',
    'leads', 'cost_per_lead',
    'registrations', 'cost_per_registration',
    'purchases', 'cost_per_purchase',
  ],
  creative: [
    'ad_name', 'ad_account_id',
    'spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr',
    'leads', 'cost_per_lead',
    'registrations', 'cost_per_registration',
    'purchases', 'cost_per_purchase',
  ],
};

// ─── Number formatters ──────────────────────────────────────────

const fmtThousands = (n: number): string =>
  n==null?'—':Math.round(n).toLocaleString('uk-UA');

const fmtMoney = (n: number): string =>
  n==null?'—':n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtPercent = (n: number): string =>
  n==null?'—':`${n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

// ─── Copy button ────────────────────────────────────────────────

function CopyBtn({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  }, [value]);

  return (
    <button
      onClick={handleCopy}
      title={`Копіювати: ${label ?? value}`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'none', border: 'none', cursor: 'pointer',
        padding: '1px 4px', marginLeft: 4,
        borderRadius: 4,
        fontSize: '0.7rem',
        color: copied ? 'var(--color-success)' : 'var(--color-text-subtle)',
        opacity: copied ? 1 : 0,
        transition: 'color 150ms ease, opacity 150ms ease',
        verticalAlign: 'middle',
      }}
      className="copy-trigger"
    >
      {copied ? '✓' : '⎘'}
    </button>
  );
}

// ─── Ads Manager link button ────────────────────────────────────

const ADS_MANAGER_BUSINESS_ID = '208668791362846';

function AdsManagerLinkBtn({ accountId, dateRange }: { accountId: string; dateRange?: { from: string; to: string } }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const today = dateRange?.from ?? new Date().toISOString().slice(0, 10);
    const todayTo = dateRange?.to ?? today;
    const url = `https://adsmanager.facebook.com/adsmanager/manage/ads?act=${accountId}&attribution_windows=default&business_id=${ADS_MANAGER_BUSINESS_ID}&columns=name%2Cdelivery%2Cbudget%2Cresults%2Ccost_per_result%2Cimpressions%2Cspend%2Cctr%2Ccpm%2Ccpc%2Cclicks%2Cactions%3Alead%2Ccost_per_action_type%3Alead%2Cactions%3Aomni_complete_registration%2Ccost_per_action_type%3Aomni_complete_registration&date=${today}_${todayTo}&global_scope_id=${ADS_MANAGER_BUSINESS_ID}&sort=spend~0`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  }, [accountId, dateRange]);

  return (
    <button
      onClick={handleCopy}
      title="Копіювати посилання на Ads Manager"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'none', border: 'none', cursor: 'pointer',
        padding: '1px 4px',
        borderRadius: 4,
        fontSize: '0.7rem',
        color: copied ? 'var(--color-success)' : 'var(--color-primary)',
        opacity: copied ? 1 : 0,
        transition: 'color 150ms ease, opacity 150ms ease',
        verticalAlign: 'middle',
      }}
      className="copy-trigger"
    >
      {copied ? '✓' : '🔗'}
    </button>
  );
}

// ─── Column definitions ─────────────────────────────────────────

interface ColDef {
  key: ColKey;
  field: SortField;
  label: string;
  align: 'left' | 'right';
  render: (row: any, dateRange?: { from: string; to: string }) => React.ReactNode;
}

const ALL_COLUMNS: ColDef[] = [
  {
    key: 'ad_name',
    field: 'ad_name',
    label: 'Ad Name',
    align: 'left',
    render: (row) => (
      <span style={{ display: 'flex', alignItems: 'center', maxWidth: 240 }} className="copy-cell">
        <span
          title={row.ad_name ?? row.ad_id}
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
        >
          {row.ad_name ?? <span style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>{row.ad_id}</span>}
        </span>
        <CopyBtn value={String(row.ad_name ?? row.ad_id)} />
      </span>
    ),
  },
  {
    key: 'ad_status',
    field: 'ad_status',
    label: 'Status',
    align: 'left',
    render: (row) => {
      if (!row.ad_status) return <span style={{ color: 'var(--color-text-subtle)' }}>—</span>;
      let color = 'var(--color-text-muted)';
      if (row.ad_status === 'ACTIVE') color = 'var(--color-success)';
      else if (['PAUSED', 'Scheduled', 'Preparing', 'Ad_OFF'].includes(row.ad_status) || row.ad_status.startsWith('Campaign_')) color = 'var(--color-warning)';
      else if (['DISAPPROVED', 'Rejected', 'BANNED', 'WITH_ISSUES', 'Policy'].includes(row.ad_status)) color = 'var(--color-danger)';
      return (
        <span style={{
          fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px',
          background: `color-mix(in srgb, ${color} 15%, transparent)`,
          color, fontWeight: 600, whiteSpace: 'nowrap'
        }}>
          {row.ad_status}
        </span>
      );
    },
  },
  {
    key: 'campaign_name',
    field: 'campaign_name',
    label: 'Campaign',
    align: 'left',
    render: (row) => row.campaign_name ? (
      <span style={{ display: 'flex', alignItems: 'center', maxWidth: 220 }} className="copy-cell">
        <span
          title={row.campaign_name}
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}
        >
          {row.campaign_name}
        </span>
        <CopyBtn value={row.campaign_name} />
      </span>
    ) : <span style={{ color: 'var(--color-text-subtle)' }}>—</span>,
  },
  {
    key: 'ad_account_id',
    field: 'ad_account_id',
    label: 'Account ID',
    align: 'left',
    render: (row, dateRange?: { from: string; to: string }) => <div style={{display:'flex',flexDirection:'column',gap:4}}>{(row.accountOwners?.length ? row.accountOwners : [{accountId:row.ad_account_id}]).map((owner:any)=>(
      <span key={owner.accountId} style={{ display: 'inline-flex', alignItems: 'center', gap: 2,flexWrap:'wrap' }} className="copy-cell">
        <span
          style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}
          title={[owner.accountName ?? row.ad_account_name ?? owner.accountId, `Timezone: ${owner.timezone ?? row.account_timezone ?? 'Unknown'}`, `Currency: ${owner.currency ?? row.budget_currency ?? 'Unknown'}`].join('\n--------------------\n')}
        >
          {owner.accountId}
        </span>
        {owner.accountId && <><CopyBtn value={owner.accountId} label={row.ad_account_name ?? owner.accountId} />
        <AdsManagerLinkBtn accountId={owner.accountId} dateRange={dateRange} /></>}
        {owner.id ? <SocialBadge id={owner.id} name={owner.name}/> : row.socialAccounts?.map((account:{id:string;name:string|null})=><SocialBadge key={account.id} {...account}/>)}
      </span>
    ))}</div>,
  },
  {
    key: 'spend',
    field: 'spend',
    label: 'Spend',
    align: 'right',
    render: (row) => (
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--color-accent)' }}>
        {fmtMoney(row.spend)}{row.metrics_state==='partial'&&<span title="Неповні дані за вибраний період" aria-label="Неповні дані"> *</span>}
      </span>
    ),
  },
  {
    key: 'impressions',
    field: 'impressions',
    label: 'Impr.',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtThousands(row.impressions)}</span>,
  },
  {
    key: 'clicks',
    field: 'clicks',
    label: 'Clicks',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtThousands(row.clicks)}</span>,
  },
  {
    key: 'cpm',
    field: 'cpm',
    label: 'CPM',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem' }}>{fmtMoney(row.cpm)}</span>,
  },
  {
    key: 'cpc',
    field: 'cpc',
    label: 'CPC',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem' }}>{fmtMoney(row.cpc)}</span>,
  },
  {
    key: 'ctr',
    field: 'ctr',
    label: 'CTR',
    align: 'right',
    render: (row) => (
      <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', color: row.ctr > 1 ? 'var(--color-success)' : 'inherit' }}>
        {fmtPercent(row.ctr)}
      </span>
    ),
  },
  {
    key: 'leads',
    field: 'leads',
    label: 'Leads',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', color: row.leads > 0 ? 'var(--color-text)' : 'var(--color-text-subtle)' }}>{fmtThousands(row.leads)}</span>,
  },
  {
    key: 'cost_per_lead',
    field: 'cost_per_lead',
    label: 'CPL',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', color: row.leads > 0 ? 'inherit' : 'var(--color-text-subtle)' }}>{row.leads > 0 ? fmtMoney(row.cost_per_lead) : '—'}</span>,
  },
  {
    key: 'registrations',
    field: 'registrations',
    label: 'Regs',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', color: row.registrations > 0 ? 'var(--color-text)' : 'var(--color-text-subtle)' }}>{fmtThousands(row.registrations)}</span>,
  },
  {
    key: 'cost_per_registration',
    field: 'cost_per_registration',
    label: 'CPReg',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', color: row.registrations > 0 ? 'inherit' : 'var(--color-text-subtle)' }}>{row.registrations > 0 ? fmtMoney(row.cost_per_registration) : '—'}</span>,
  },
  {
    key: 'purchases',
    field: 'purchases',
    label: 'Purch.',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', color: row.purchases > 0 ? 'var(--color-text)' : 'var(--color-text-subtle)' }}>{fmtThousands(row.purchases)}</span>,
  },
  {
    key: 'cost_per_purchase',
    field: 'cost_per_purchase',
    label: 'CPP',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', color: row.purchases > 0 ? 'inherit' : 'var(--color-text-subtle)' }}>{row.purchases > 0 ? fmtMoney(row.cost_per_purchase) : '—'}</span>,
  },
  {
    key: 'app_installs',
    field: 'app_installs',
    label: 'App Inst.',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-subtle)' }}>{fmtThousands(row.app_installs)}</span>,
  },
  {
    key: 'cost_per_app_install',
    field: 'cost_per_app_install',
    label: 'CPI',
    align: 'right',
    render: (_row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', color: 'var(--color-text-subtle)' }}>—</span>,
  },
  {
    key: 'budget',
    field: 'campaign_budget',
    label: 'Budget (USD)',
    align: 'right',
    render: (_row) => null, // handled specially in tbody
  }
];

// ─── Sort arrow ─────────────────────────────────────────────────

function SortArrow({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) {
  return (
    <span style={{ display: 'inline-block', marginLeft: 3, opacity: active ? 1 : 0.25, fontSize: '0.65rem' }}>
      {active && direction === 'asc' ? '▲' : '▼'}
    </span>
  );
}

// ─── Preset buttons ─────────────────────────────────────────────

const PRESET_LABELS: Record<string, string> = {
  today: 'Сьогодні',
  yesterday: 'Вчора',
  last7: '7 днів',
  last30: '30 днів',
};

// ─── Currency symbol ────────────────────────────────────────────

const getCurrencySymbol = (currencyCode: string | null) => {
  if (currencyCode === 'USD') return '$';
  if (currencyCode === 'EUR') return '€';
  if (currencyCode === 'UAH') return '₴';
  return currencyCode || '';
};

// ─── Tag chip ───────────────────────────────────────────────────

function TagChip({ tag, onRemove, operator = 'contains' }: { tag: string; onRemove: () => void; operator?: string }) {
  return (
    <div
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '3px 10px', borderRadius: 'var(--radius-full)',
        background: 'var(--color-accent-dim)',
        border: '1px solid var(--color-accent)',
        color: 'var(--color-accent)',
        fontSize: '0.75rem', fontWeight: 600,
        userSelect: 'none', whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontFamily: 'var(--font-mono)', opacity: 0.6, fontSize: '0.65rem', marginRight: 2 }}>{operator}</span>
      {tag}
      <span
        onClick={onRemove}
        style={{ marginLeft: 2, cursor: 'pointer', opacity: 0.7, fontWeight: 'bold', fontSize: '0.8rem' }}
        title="Видалити тег"
      >
        ✕
      </span>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────

export function AdsStatsPage() {
  const {
    activeTab,
    setActiveTab,
    report,
    reportError,
    fetchAllReports,
    loading,
    syncing,
    syncProgress,
    evaluationPending,
    cancelSync,
    cancelling,
    cancelRequested,
    lastSync,
    needsWide,
    dateRange,
    setDateRange,
    sync,
    setCampaignOpen,
    patchReportRow,
    refreshAfterMutation,
    invalidateReports,creativeFilter,setCreativeFilter,
    filterOptions,
    campaignBadgeFilter, setCampaignBadgeFilter,
    socialBadgeFilter, setSocialBadgeFilter,
    sortConfig,
    toggleSort,
    accountFilter,
    setAccountFilter,
    searchQuery,
    setSearchQuery,
    tagFilters,
    addTagFilter,
    removeTagFilter,
    statusFilter,
    setStatusFilter,
    metricFilters,
    setMetricFilters,
    activeReportNormalized,
    openCreativeCampaigns,
  } = useAdsStats();

  const [visibleRowCount, setVisibleRowCount] = useState(100);
  const reportScroller = useRef<HTMLDivElement>(null);
  const reportSentinel = useRef<HTMLTableRowElement>(null);
  const reportRenderKey = report.map(row => row.rowKey).join('\u0000');
  useEffect(() => { setVisibleRowCount(100); }, [reportRenderKey, activeTab, sortConfig.field, sortConfig.direction]);
  useEffect(() => {
    const sentinel = reportSentinel.current;
    if (!sentinel || visibleRowCount >= report.length) return;
    const observer = new IntersectionObserver(entries => {
      if(entries.some(entry => entry.isIntersecting))setVisibleRowCount(count => Math.min(count + 100, report.length));
    }, { root: reportScroller.current, rootMargin: '0px 0px 200px 0px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleRowCount, report.length, loading]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [visibleColsState, setVisibleColsState] = usePersistentState<Record<TabKey, ColKey[]>>('stats.columns', {
    ads: [...DEFAULT_VISIBLE_PER_TAB.ads], campaigns: [...DEFAULT_VISIBLE_PER_TAB.campaigns],
    geo: [...DEFAULT_VISIBLE_PER_TAB.geo], accounts: [...DEFAULT_VISIBLE_PER_TAB.accounts], creative: [...DEFAULT_VISIBLE_PER_TAB.creative],
  }, (value): value is Record<TabKey, ColKey[]> => !!value && typeof value === 'object'
    && ['ads', 'campaigns', 'geo', 'accounts', 'creative'].every((tab) => Array.isArray((value as Record<string, unknown>)[tab])
      && ((value as Record<string, unknown>)[tab] as unknown[]).every((key) => ALL_COLUMNS.some((column) => column.key === key))));
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [filtersPanelOpen, setFiltersPanelOpen] = usePersistentState('stats.filters-panel', false);
  const campaignBadgeOptions = [...new Set([...campaignBadgeFilter, ...filterOptions.campaignBadges, ...activeReportNormalized.flatMap(row => row.campaignBadges ?? [])])].filter(value => isCampaignBadge(value)).sort();
  const socialBadgeOptions = [...new Map([
    ...socialBadgeFilter.map(id => [id, {id, name: null}] as const),
    ...filterOptions.socialAccounts.map(account=>[account.id,account] as const),
    ...activeReportNormalized.flatMap(row => (row.socialAccounts ?? []).map(account => [account.id, account] as const)),
  ]).values()].sort((a,b) => (a.name || '').localeCompare(b.name || ''));
  const badgeFilterCount = campaignBadgeFilter.length + socialBadgeFilter.length;
  const advancedFilterCount = statusFilter.length + metricFilters.length + badgeFilterCount;
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [triggerExecutionId,setTriggerExecutionId]=useState<string|null>(null);
  const consumeTriggerExecution=useCallback(()=>setTriggerExecutionId(null),[]);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [copyColumnMenuOpen, setCopyColumnMenuOpen] = useState(false);
  const [editingBudgetRowId, setEditingBudgetRowId] = useState<string | null>(null);
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [budgetInputValue, setBudgetInputValue] = useState<string>('');
  const lastCheckedIndexRef = useRef<number | null>(null);

  const { showToast } = useToast();


  // ── Selectable Total ─────────────────────────────────────────
  const getRowKey = (row: AdsReportRow): string => row.rowKey;
  const { toggle: toggleSelection, reset: resetSelection, isSelected, activeRows, hasSelection } = useSelectableTotal(report, getRowKey);
  useEffect(() => {setSelectedIds(new Set());lastCheckedIndexRef.current=null;resetSelection();},[activeTab,dateRange.from,dateRange.to,accountFilter,campaignBadgeFilter,socialBadgeFilter,statusFilter,searchQuery,tagFilters,metricFilters,resetSelection]);

  const getActionSelectionKey = (row: AdsReportRow): string | null =>
    (row.entityRef && /^\d+$/.test(row.entityRef.id)) || (row.rowKind==='creative' && row.creative_id) ? row.rowKey : null;
  const handleCopyColumn = async (colKey: ColKey) => {
    const lines = report
      .filter((r: any) => {
        const id = getActionSelectionKey(r);
        return id && selectedIds.has(id);
      })
      .map((r: any) => {
        if (colKey === 'budget') {
          if (activeTab === 'campaigns') return r.campaign_budget ? String(r.campaign_budget).replace('.', ',') : 'ABO';
          if (activeTab === 'ads') return (r.campaign_budget || r.adset_budget) ? String(r.campaign_budget || r.adset_budget).replace('.', ',') : '';
          return '';
        }
        if (colKey === 'ad_name') return String(r.ad_name ?? r.ad_id ?? '');

        const val = r[colKey];
        if (typeof val === 'number') return val.toString().replace('.', ',');
        return val ? String(val) : '';
      })
      .join('\n');

    try {
      await navigator.clipboard.writeText(lines);
      showToast('Скопійовано колонку', 'success');
    } catch {
      showToast('Помилка копіювання', 'error');
    }
    setActionsMenuOpen(false);
    setCopyColumnMenuOpen(false);
  };

  const handleAction = async (action: 'ACTIVE' | 'PAUSED') => {
    setActionsMenuOpen(false);
    let successCount = 0;
    for (const key of selectedIds) {
      const row=report.find(r=>r.rowKey===key);const ref=row?.entityRef;
      if(!ref || !['ad','campaign'].includes(ref.type))continue;
      try {
        await api.post('/api/ads-stats/update-status',{id:ref.id,ad_account_id:ref.adAccountId,status:action,idType:ref.type});
        const status=['Policy','Unsettled'].includes(row.ad_status??'')?row.ad_status:action;
        patchReportRow(activeTab,ref.type==='campaign'?'campaign_id':'ad_id',ref.id,{configured_status:action,ad_status:status},ref.adAccountId);
        successCount++;
      }catch(err){showToast(`Помилка для ${ref.id}: ${err instanceof ApiClientError ? err.message : String(err)}`,'error');}
    }
    if(successCount>0){showToast(`Оновлено статус для ${successCount} елементів`,'success');void refreshAfterMutation();}
    setSelectedIds(new Set());
  };

  const handleBudgetSave = async (id: string, type: 'daily' | 'lifetime', matchField: 'campaign_id' | 'adset_id', accountId:string) => {
    const val = Number(budgetInputValue);
    if (!Number.isSafeInteger(val) || val <= 0 || budgetSaving) {
      showToast('Невірне значення бюджету', 'error');
      return;
    }
    const idType = matchField === 'campaign_id' ? 'campaign' : 'adset';
    setBudgetSaving(true);
    try {
      const row = report.find((r: any) => r[matchField] === id && r.ad_account_id===accountId);
      const adAccountId = row?.ad_account_id;
      const result = await api.post<{ budget: number }>('/api/ads-stats/update-budget', { id, ad_account_id: adAccountId, budget: val, type, idType, budget_usd_rate: row?.budget_usd_rate, budget_currency: row?.budget_currency });
      showToast('Бюджет оновлено', 'success');
      setEditingBudgetRowId(null);
      const patch = matchField === 'campaign_id'
        ? { campaign_budget: result.budget, campaign_budget_type: type }
        : { adset_budget: result.budget, adset_budget_type: type };
      patchReportRow('campaigns', matchField, id, patch,accountId);
      patchReportRow('ads', matchField, id, patch,accountId);
    } catch (err) {
      showToast(`Помилка: ${err instanceof ApiClientError ? err.message : String(err)}`, 'error');
    } finally {
      setBudgetSaving(false);
    }
  };

  const filterCount = accountFilter ? accountFilter.length : 0;
  const presets = getPresets();
  const rawCurrency = 'USD';
  const currencySymbol = getCurrencySymbol(rawCurrency);

  const visibleCols = new Set(visibleColsState[activeTab]);
  const visibleColumns = ALL_COLUMNS.filter((c) => visibleCols.has(c.key));

  const toggleCol = (c: ColKey) => {
    setVisibleColsState(prev => {
      const nextTabCols = new Set(prev[activeTab]);
      if (nextTabCols.has(c)) nextTabCols.delete(c);
      else nextTabCols.add(c);
      return { ...prev, [activeTab]: [...nextTabCols] };
    });
  };

  // ── Totals from activeRows ────────────────────────────────────
  const summary=calculateReportTotals(activeRows);
  const {spend:totalSpend,impressions:totalImpr,clicks:totalClicks,leads:totalLeads,registrations:totalRegs,purchases:totalPurchases}=summary;

  const totalRowCountLabel = hasSelection
    ? `${activeRows.length} обр. з ${report.length}`
    : `${report.length}`;

  const totals: Record<ColKey, React.ReactNode> = {
    ad_name: <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>Разом ({totalRowCountLabel}){summary.incomplete&&summary.hasData?' *':''}</span>,
    ad_status: null,
    campaign_name: null,
    ad_account_id: null,
    spend: <span style={{ color: 'var(--color-accent)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(totalSpend)}</span>,
    impressions: <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtThousands(totalImpr)}</span>,
    clicks: <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtThousands(totalClicks)}</span>,
    cpm: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(totalImpr > 0 ? (totalSpend / totalImpr) * 1000 : 0)}</span>,
    cpc: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(totalClicks > 0 ? totalSpend / totalClicks : 0)}</span>,
    ctr: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtPercent(totalImpr > 0 ? (totalClicks / totalImpr) * 100 : 0)}</span>,
    leads: <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtThousands(totalLeads)}</span>,
    cost_per_lead: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{totalLeads > 0 ? fmtMoney(totalSpend / totalLeads) : '—'}</span>,
    registrations: <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtThousands(totalRegs)}</span>,
    cost_per_registration: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{totalRegs > 0 ? fmtMoney(totalSpend / totalRegs) : '—'}</span>,
    purchases: <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtThousands(totalPurchases)}</span>,
    cost_per_purchase: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{totalPurchases > 0 ? fmtMoney(totalSpend / totalPurchases) : '—'}</span>,
    app_installs: <span style={{ color: 'var(--color-text-subtle)' }}>—</span>,
    cost_per_app_install: <span style={{ color: 'var(--color-text-subtle)' }}>—</span>,
    budget: null,
  };
  const totalLabelColumn=visibleColumns.find(col=>['ad_name','campaign_name','ad_account_id'].includes(col.key));
  if(totalLabelColumn&&totalLabelColumn.key!=='ad_name') {
    totals[totalLabelColumn.key]=totals.ad_name;totals.ad_name=null;
  }
  if(!summary.hasData)for(const field of ['spend','impressions','clicks','cpm','cpc','ctr','leads','cost_per_lead','registrations','cost_per_registration','purchases','cost_per_purchase'] as ColKey[])totals[field]='—';

  const handleExport = async () => {
    try {
      const allReports = await fetchAllReports();
      exportAdsReportToXlsx(allReports, dateRange.from, dateRange.to);
    } catch {
      // fetchAllReports already displays the API error; do not export partial data.
    }
  };

  // ── Status counts for indicator badges ───────────────────────
  const statusCounts = activeReportNormalized.reduce((acc: Record<string, number>, row) => {
    if (row.ad_status) {
      acc[row.ad_status] = (acc[row.ad_status] ?? 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  // ── Selected account IDs for targeted sync ────────────────────
  const selectedAccountIds = selectedIds.size > 0
    ? [...new Set(report
        .filter((r) => {
          const fbId = getActionSelectionKey(r);
          return fbId && selectedIds.has(fbId);
        })
        .map((r) => r.ad_account_id)
        .filter(Boolean))]
    : [];

  // ── Handle Enter on search → add tag filter ───────────────────
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = searchQuery.trim();
      if (val) {
        addTagFilter(val);
        setSearchQuery('');
      }
      e.preventDefault();
    }
  };

  return (
    <div className="page-body">
      {/* ── Page header ─────────────────────────────────── */}
      <div style={{ marginBottom: 'var(--space-3)' }}>
        <div className="stats-report-heading">
        <h1 style={{ fontSize: '1.5rem', margin: 0 }}>Звіти</h1>
        {syncing && syncProgress && (
          <div role="status" aria-live="polite" className="stats-sync-status" title={`Кількість завдань на етапі, а не одночасних запитів. ${syncProgress.stageSummary ?? ''}`}>
            Опубліковано: {syncProgress.completed}/{syncProgress.total} · очікують старту: {syncProgress.queued} · Meta формує звіт: {syncProgress.waitingMeta} · збір метрик: {syncProgress.insightsDownloading ?? syncProgress.downloading} · збір статусів/бюджетів: {syncProgress.metadataDownloading ?? 0} · готові до запису: {syncProgress.publishing} · пауза: {syncProgress.retry ?? 0} · помилки: {syncProgress.failed}
            {syncProgress.waitSummary && <span> · {syncProgress.waitSummary}</span>}
            {syncProgress.errorSummary && <span> · {syncProgress.errorSummary}</span>}
          </div>
        )}
        {!syncing && evaluationPending!==null && (
          <div role="status" aria-live="polite" className="stats-sync-status">
            {evaluationPending>0?`Статистику опубліковано. Фонова перевірка тригерів: залишилось ${evaluationPending}.`:'Фонову перевірку тригерів завершено.'}
          </div>
        )}
        {/* Last sync info */}
        {lastSync && !syncing && (
          <div style={{
            fontSize: '0.75rem', color: 'var(--color-text-muted)',
            textAlign: 'right',
            marginLeft: 'auto',
          }}>
            Останній sync: {new Date(lastSync.createdAt).toLocaleString('uk-UA')} ·
            {' '}{lastSync.accountsSuccess ?? 0} OK · {lastSync.accountsFailed ?? 0} помилок
            {(lastSync.durationMs ?? 0) > 0 && ` · ${((lastSync.durationMs ?? 0) / 1000).toFixed(1)}с`}
            {needsWide === true && <span title="Sync спочатку оновить відбір кампаній, потім деталізацію"> · Потрібен Wide</span>}
          </div>
        )}
        {!lastSync && !syncing && needsWide === true && <span className="stats-sync-status" title="Sync спочатку оновить відбір кампаній, потім деталізацію">Потрібен Wide</span>}

      </div>
        {reportError && <div role="alert" className="notification notification--error" style={{ marginTop: 12 }}>{reportError} <Link to="/currency-rates">Налаштувати курси</Link></div>}
      </div>

      {/* ── Toolbar ─────────────────────────────────────── */}
      <div
        className="glass-card"
        style={{
          position: 'relative',
          zIndex: 100,
          padding: 'var(--space-3) var(--space-4)',
          marginBottom: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          overflow: 'visible',  // Keep toolbar menus visible
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          {/* Preset buttons */}
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            {Object.entries(PRESET_LABELS).map(([key, label]) => {
              const preset = presets[key as keyof typeof presets];
              const isActive = dateRange.from === preset.from && dateRange.to === preset.to;
              return (
                <button
                  key={key}
                  className={`btn ${isActive ? 'btn--primary' : 'btn--ghost'}`}
                  style={{ padding: '4px 10px', fontSize: '0.8125rem' }}
                  onClick={() => setDateRange(preset)}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Date range inputs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <input
              id="ads-date-from"
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              className="filter-select"
              style={{ minWidth: 140 }}
            />
            <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>—</span>
            <input
              id="ads-date-to"
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              className="filter-select"
              style={{ minWidth: 140 }}
            />
          </div>

          <div className="stats-account-actions">
          {/* Account filter button */}
          <button
            id="accounts-filter-btn"
            className={`btn ${filterCount > 0 ? 'btn--primary' : 'btn--secondary'}`}
            onClick={() => setFilterModalOpen(true)}
            style={filterCount > 0 ? { boxShadow: '0 0 10px var(--color-primary-glow)' } : {}}
          >
            Список акаунтів
            {filterCount > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--color-accent)', color: '#000',
                fontSize: '0.7rem', fontWeight: 700,
                borderRadius: 'var(--radius-full)', minWidth: 18, height: 18,
                padding: '0 5px', marginLeft: 'var(--space-1)',
              }}>
                {filterCount}
              </span>
            )}
          </button>

        <TriggerFeature selectedRows={report.filter(row=>selectedIds.has(row.rowKey))} accountFilter={accountFilter} executionId={triggerExecutionId} onExecutionConsumed={consumeTriggerExecution} onChanged={invalidateReports} onSync={ids=>{if(ids.length)void sync(ids);}}/>
          </div>

          {/* ── Filters Dropdown ──────────────────────── */}
          <div style={{ position: 'relative' }}>
            <button
              className={`btn ${advancedFilterCount > 0 ? 'btn--primary' : 'btn--ghost'}`}
              style={{ fontSize: '0.8125rem', padding: '4px 10px' }}
              onClick={() => setFiltersPanelOpen(o => !o)}
            >
              Фільтри {advancedFilterCount > 0 && `(${advancedFilterCount})`}
            </button>

            {filtersPanelOpen && (
              <>
                {/* Backdrop */}
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
                  onClick={() => setFiltersPanelOpen(false)}
                />
                <div
                  className="glass-card"
                  style={{
                    position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                    zIndex: 9999,
                    background: 'var(--color-surface)',
                    padding: 'var(--space-4)',
                    width: 'min(340px, calc(100vw - 32px))',
                    maxHeight: 480,
                    overflowY: 'auto',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.7)',
                    display: 'flex', flexDirection: 'column', gap: 'var(--space-3)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>Фільтри</span>
                    <button
                      className="btn btn--ghost"
                      style={{ padding: '0 4px', fontSize: '1rem', lineHeight: 1 }}
                      onClick={() => setFiltersPanelOpen(false)}
                    >
                      ✕
                    </button>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 'var(--space-1)' }}>Статус</div>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                      {['ACTIVE', 'PAUSED', 'Rejected', 'Scheduled', 'Preparing', 'Not delivering', 'Policy', 'Unsettled'].map(status => (
                        <label key={status} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: '0.8125rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={statusFilter.includes(status)}
                            onChange={(e) => {
                              if (e.target.checked) setStatusFilter([...statusFilter, status]);
                              else setStatusFilter(statusFilter.filter(s => s !== status));
                            }}
                          />
                          {status}
                        </label>
                      ))}
                    </div>
                  </div>


                  <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-2)' }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 'var(--space-2)' }}>Метрики</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                      {metricFilters.map((filter, i) => (
                        <div key={i} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                          <select
                            className="filter-select"
                            value={filter.field}
                            onChange={(e) => {
                              const newFilters = [...metricFilters];
                              newFilters[i].field = e.target.value;
                              setMetricFilters(newFilters);
                            }}
                            style={{ fontSize: '0.8125rem', minWidth: 120 }}
                          >
                            {ALL_COLUMNS.filter(c => ['spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr', 'leads', 'cost_per_lead', 'registrations', 'cost_per_registration', 'purchases', 'cost_per_purchase', 'app_installs', 'cost_per_app_install'].includes(c.key)).map(c => (
                              <option key={c.key} value={c.key}>{c.label}</option>
                            ))}
                          </select>
                          <select
                            className="filter-select"
                            value={filter.operator}
                            onChange={(e) => {
                              const newFilters = [...metricFilters];
                              newFilters[i].operator = e.target.value as '>=' | '<=' | '=';
                              setMetricFilters(newFilters);
                            }}
                            style={{ fontSize: '0.8125rem', width: 54 }}
                          >
                            <option value=">=">{'>='}</option>
                            <option value="<=">{'<='}</option>
                            <option value="=">{'='}</option>
                          </select>
                          <input
                            type="number"
                            className="filter-select"
                            value={filter.value}
                            onChange={(e) => {
                              const newFilters = [...metricFilters];
                              newFilters[i].value = Number(e.target.value);
                              setMetricFilters(newFilters);
                            }}
                            style={{ fontSize: '0.8125rem', width: 80, padding: '4px 6px' }}
                          />
                          <button
                            className="btn btn--ghost"
                            style={{ padding: '4px', color: 'var(--color-danger)', flexShrink: 0 }}
                            onClick={() => setMetricFilters(metricFilters.filter((_, idx) => idx !== i))}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      className="btn btn--secondary"
                      style={{ fontSize: '0.75rem', padding: '4px 10px', marginTop: 'var(--space-2)' }}
                      onClick={() => setMetricFilters([...metricFilters, { field: 'purchases', operator: '>=', value: 1 }])}
                    >
                      + Додати фільтр метрики
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-2)' }}>
                    <button className="btn btn--ghost" style={{ fontSize: '0.8125rem' }} onClick={() => { setStatusFilter([]); setMetricFilters([]); setCampaignBadgeFilter([]);setSocialBadgeFilter([]); }}>
                      Скинути
                    </button>
                    <button className="btn btn--primary" style={{ fontSize: '0.8125rem' }} onClick={() => setFiltersPanelOpen(false)}>
                      Готово
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Actions Menu */}
          <div style={{ position: 'relative' }}>
            <button
              className={`btn ${selectedIds.size > 0 ? 'btn--primary' : 'btn--ghost'}`}
              style={{ fontSize: '0.8125rem', padding: '4px 10px' }}
              disabled={selectedIds.size === 0 || !['ads','campaigns'].includes(activeTab)}
              title={activeTab === 'geo' || activeTab === 'accounts' || activeTab === 'creative' ? 'Дії недоступні для цього звіту' : undefined}
              onClick={() => setActionsMenuOpen(!actionsMenuOpen)}
            >
              Actions {selectedIds.size > 0 && `(${selectedIds.size})`}
            </button>
            {actionsMenuOpen && selectedIds.size > 0 && (
              <div
                className="glass-card"
                style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100,
                  padding: 'var(--space-2) 0', width: 160,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  display: 'flex', flexDirection: 'column'
                }}
              >
                <button
                  className="btn btn--ghost"
                  style={{ textAlign: 'left', borderRadius: 0, padding: '8px 16px', justifyContent: 'flex-start' }}
                  onClick={() => handleAction('ACTIVE')}
                >
                  Увімкнути
                </button>
                <button
                  className="btn btn--ghost"
                  style={{ textAlign: 'left', borderRadius: 0, padding: '8px 16px', justifyContent: 'flex-start' }}
                  onClick={() => handleAction('PAUSED')}
                >
                  Вимкнути
                </button>
                <div style={{ position: 'relative' }}>
                  <button
                    className="btn btn--ghost"
                    style={{ width: '100%', textAlign: 'left', borderRadius: 0, padding: '8px 16px', justifyContent: 'space-between', display: 'flex' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCopyColumnMenuOpen(!copyColumnMenuOpen);
                    }}
                  >
                    Copy Column <span>{copyColumnMenuOpen ? '◀' : '▶'}</span>
                  </button>
                  {copyColumnMenuOpen && (
                    <div
                      className="glass-card"
                      style={{
                        position: 'absolute', top: 0, left: '100%', marginLeft: '4px', zIndex: 101,
                        padding: 'var(--space-2) 0', width: 180, maxHeight: 300, overflowY: 'auto',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column'
                      }}
                    >
                      {visibleColumns.map(col => (
                        <button
                          key={col.key}
                          className="btn btn--ghost"
                          style={{ textAlign: 'left', borderRadius: 0, padding: '6px 16px', justifyContent: 'flex-start', fontSize: '0.8125rem' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyColumn(col.key);
                          }}
                        >
                          {col.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Column picker */}
          <button
            className="btn btn--ghost"
            style={{ fontSize: '0.8125rem', padding: '4px 10px' }}
            onClick={() => setColPickerOpen(!colPickerOpen)}
          >
            Колонки
          </button>

          {/* Export and Sync */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-2)' }}>
            {activeTab === 'campaigns' && (
              <button
                className="btn btn--secondary"
                onClick={() => setCopyModalOpen(true)}
                style={{ fontSize: '0.875rem' }}
                disabled={loading}
              >
                Copy
              </button>
            )}
            <button
              id="ads-export-btn"
              className="btn btn--secondary"
              onClick={handleExport}
              style={{ fontSize: '0.875rem' }}
              disabled={loading}
            >
              XLSX
            </button>
            <button id="ads-wide-sync-btn" className="btn btn--secondary" disabled={syncing}
              onClick={() => sync(selectedAccountIds.length > 0 ? selectedAccountIds : undefined,'wide')}
              title="Оновити відбір кампаній і метрики періоду; без деталізації оголошень">Wide sync</button>
            <button
              id="ads-sync-btn"
              className={`btn ${selectedAccountIds.length > 0 ? 'btn--secondary' : 'btn--primary'}`}
              disabled={syncing}
              onClick={() => sync(selectedAccountIds.length > 0 ? selectedAccountIds : undefined)}
              style={{ minWidth: 80 }}
              title={selectedAccountIds.length > 0 ? `Sync ${selectedAccountIds.length} account(s)` : 'Sync all accounts'}
            >
              {syncing ? `Sync… ${syncProgress ? `${syncProgress.completed}/${syncProgress.total}` : ''}` : selectedAccountIds.length > 0 ? `Sync (${selectedAccountIds.length})` : 'Sync'}
            </button>
            {syncing && <button id="ads-sync-cancel-btn" className="btn btn--secondary" onClick={() => void cancelSync()} disabled={cancelling}>
              {cancelling || cancelRequested ? 'Зупиняю…' : 'Зупинити'}
            </button>}
          </div>
        </div>



        {/* Search + Tag filters row */}
        <div style={{ display: 'flex', gap: 'var(--space-3)', width: '100%', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Search input */}
          <div style={{ flex: '0 0 auto', minWidth: 260 }}>
            <input
              type="text"
              placeholder="Пошук… (Enter — додати тег-фільтр)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="filter-input"
              style={{
                width: '100%', padding: '8px 12px', fontSize: '0.875rem',
                borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)',
                background: 'var(--color-surface-2)', color: 'var(--color-text)'
              }}
            />
          </div>

          {/* Exact creative filter never uses campaign-name substrings. */}
          {creativeFilter && (activeTab==='campaigns' || activeTab==='ads') && <div style={{display:'flex',alignItems:'center',gap:'var(--space-2)',marginBottom:'var(--space-2)'}}>
            <span>Крео: {creativeTokenFromId(creativeFilter)}</span>
            <button className="btn btn--ghost" aria-label="Зняти фільтр крео" onClick={()=>{setCreativeFilter(null);setSearchQuery('');}}>Зняти фільтр</button>
          </div>}
          {/* Active tag filters + status/metric chips */}
          <div style={{ flex: 1, display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Tag filters (from search) */}
            {tagFilters.map((tag) => (
              <TagChip key={tag} tag={tag} onRemove={() => removeTagFilter(tag)} />
            ))}
            {<>
              {campaignBadgeFilter.map(badge => <TagChip key={'campaign:'+badge} operator="=" tag={'Кампанія: '+badge} onRemove={()=>setCampaignBadgeFilter(campaignBadgeFilter.filter(value=>value!==badge))}/>)}
              {socialBadgeFilter.map(id => <TagChip key={'social:'+id} operator="=" tag={socialBadgeOptions.find(account=>account.id===id)?.name || 'Без назви'} onRemove={()=>setSocialBadgeFilter(socialBadgeFilter.filter(value=>value!==id))}/>)}
            </>}

            {/* Status filter chip */}
            {statusFilter.length > 0 && (
              <div
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px',
                  background: 'var(--color-primary)', color: '#fff',
                  borderRadius: 'var(--radius-full)', fontSize: '0.75rem',
                  cursor: 'pointer', userSelect: 'none',
                }}
                onClick={() => setFiltersPanelOpen(true)}
              >
                <span style={{ fontWeight: 600 }}>Статус:</span> {statusFilter.length} обр.
                <span
                  onClick={(e) => { e.stopPropagation(); setStatusFilter([]); }}
                  style={{ marginLeft: 2, cursor: 'pointer', opacity: 0.7, fontWeight: 'bold' }}
                >
                  ✕
                </span>
              </div>
            )}

            {/* Metric filter chips */}
            {metricFilters.map((f, i) => {
              const label = ALL_COLUMNS.find(c => c.key === f.field)?.label || f.field;
              return (
                <div
                  key={i}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px',
                    background: 'var(--color-primary)', color: '#fff',
                    borderRadius: 'var(--radius-full)', fontSize: '0.75rem',
                    cursor: 'pointer', userSelect: 'none',
                  }}
                  onClick={() => setFiltersPanelOpen(true)}
                >
                  <span style={{ fontWeight: 600 }}>{label}</span> {f.operator} {f.value}
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setMetricFilters(metricFilters.filter((_, idx) => idx !== i));
                    }}
                    style={{ marginLeft: 2, cursor: 'pointer', opacity: 0.7, fontWeight: 'bold' }}
                  >
                    ✕
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {activeTab==='geo'&&<p style={{color:'var(--color-text-muted)'}}>ALL ? aggregate without delivered-country breakdown. Historical GEO facts retain their own coverage.</p>}
        {/* Status indicator badges — always visible when data exists */}
        {(Object.entries(statusCounts).length > 0 || campaignBadgeOptions.length > 0 || socialBadgeOptions.length > 0) && (
          <div className="stats-badge-filters">
            <span style={{ fontSize: '0.72rem', color: 'var(--color-text-subtle)', marginRight: 2 }}>Статус:</span>
            <button type="button" className="btn btn--ghost btn--sm" aria-pressed={!statusFilter.length} onClick={()=>setStatusFilter([])}>Всі</button>
            {Object.entries(statusCounts)
              .sort(([, a], [, b]) => b - a)
              .map(([status, count]) => {
                let color = 'var(--color-text-muted)';
                if (status === 'ACTIVE') color = 'var(--color-success)';
                else if (['PAUSED', 'Scheduled', 'Preparing', 'Ad_OFF'].includes(status) || status.startsWith('Campaign_')) color = 'var(--color-warning)';
                else if (['DISAPPROVED', 'Rejected', 'BANNED', 'WITH_ISSUES', 'Policy'].includes(status)) color = 'var(--color-danger)';
                const isActive = statusFilter.includes(status);
                return (
                  <button type="button" aria-pressed={isActive}
                    key={status}
                    style={{
                      fontSize: '0.72rem', padding: '2px 8px', borderRadius: 'var(--radius-full)',
                      background: isActive ? color : `color-mix(in srgb, ${color} 15%, transparent)`,
                      color: isActive ? '#fff' : color,
                      fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
                      border: `1px solid ${color}`,
                      transition: 'all 150ms ease',
                    }}
                    onClick={() => {
                      if (statusFilter.includes(status)) setStatusFilter(statusFilter.filter(s => s !== status));
                      else setStatusFilter([...statusFilter, status]);
                    }}
                    title={isActive ? `Зняти фільтр ${status}` : `Фільтр по ${status}`}
                  >
                    {count} {status}
                  </button>
                );
              })}
            {[{label:'Бейдж кампанії',values:campaignBadgeOptions.map(value=>({id:value,label:value})),selected:campaignBadgeFilter,set:setCampaignBadgeFilter},
              {label:'Соц акаунт',values:socialBadgeOptions.map(value=>({id:value.id,label:value.name || 'Без назви'})),selected:socialBadgeFilter,set:setSocialBadgeFilter}].map(group=>(
              <div key={group.label} role="group" aria-label={group.label} className="stats-badge-filter-group">
                <span className="text-xs text-muted">{group.label}:</span>
                <button type="button" className="btn btn--ghost btn--sm" aria-pressed={!group.selected.length} onClick={()=>group.set([])}>Всі</button>
                {group.values.map(value=><button key={value.id} type="button" aria-pressed={group.selected.includes(value.id)}
                  className={'btn btn--sm '+(group.selected.includes(value.id)?'btn--primary':'btn--ghost')}
                  onClick={()=>group.set(group.selected.includes(value.id)?group.selected.filter(id=>id!==value.id):[...group.selected,value.id])}>{value.label}</button>)}
              </div>
            ))}
          </div>
        )}
      </div>



      {/* ── Column picker dropdown ────────────────────────── */}
      {colPickerOpen && (
        <div
          className="glass-card"
          style={{
            padding: 'var(--space-3) var(--space-4)',
            marginBottom: 'var(--space-3)',
            display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)',
          }}
        >
          {ALL_COLUMNS.map((col) => (
            <label
              key={col.key}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
                fontSize: '0.8125rem', cursor: 'pointer', userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={visibleCols.has(col.key)}
                onChange={() => toggleCol(col.key)}
              />
              {col.label}
            </label>
          ))}
        </div>
      )}

      {/* ── Tabs ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-3)', borderBottom: '1px solid var(--color-border)' }}>
        {[
          { key: 'campaigns', label: 'По кампаніях' },
          { key: 'ads', label: 'По оголошеннях' },
          { key: 'geo', label: 'По GEO' },
          { key: 'accounts', label: 'По кабінетах' },
          { key: 'creative', label: 'По крео' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as TabKey)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 'var(--space-2) 0', fontSize: '0.9375rem', fontWeight: 600,
              color: activeTab === tab.key ? 'var(--color-primary)' : 'var(--color-text-muted)',
              borderBottom: activeTab === tab.key ? '2px solid var(--color-primary)' : '2px solid transparent',
              transition: 'all var(--transition-fast)',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Table ────────────────────────────────────────── */}
      <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading&&report.length===0 ? (
          <div style={{ padding: 'var(--space-12)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            <div className="spinner" style={{ width: 32, height: 32, margin: '0 auto var(--space-3)' }} />
            <div>Завантаження...</div>
          </div>
        ) : report.length === 0 ? (
          <div className="empty-state" style={{ padding: 'var(--space-12)' }}>
            <div className="empty-state__title">Немає даних за {dateRange.from === dateRange.to ? dateRange.from : `${dateRange.from} — ${dateRange.to}`}</div>
            <div className="empty-state__desc">Натисни «Синхронізувати» щоб завантажити статистику.</div>
          </div>
        ) : (
          <div ref={reportScroller} style={{ overflow: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
            {/* Reset selection bar */}
            {hasSelection && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-4)',
                background: 'color-mix(in srgb, var(--color-primary) 8%, var(--color-surface-2))',
                borderBottom: '1px solid color-mix(in srgb, var(--color-primary) 25%, var(--color-border))',
                fontSize: '0.8125rem', color: 'var(--color-text-muted)',
              }}>
                <span>Вибрано {activeRows.length} рядк{activeRows.length === 1 ? 'ів' : activeRows.length < 5 ? 'ки' : 'ів'} — Total перераховано</span>
                <button
                  className="btn btn--ghost"
                  style={{ fontSize: '0.75rem', padding: '2px 10px', marginLeft: 'auto' }}
                  onClick={resetSelection}
                >
                  Скинути вибір
                </button>
              </div>
            )}

            {summary.incomplete&&<div role="status" className="report-incomplete-summary" style={{padding:'var(--space-2) var(--space-4)',fontSize:'0.8125rem',color:'var(--color-text-muted)',borderBottom:'1px solid var(--color-border)'}}>
              {summary.hasData?`* Total за наявними даними. Неповні або відсутні метрики: ${summary.unknownRows+summary.partialRows} із ${activeRows.length} рядків.`:`Немає збережених метрик за ${dateRange.from}${dateRange.to!==dateRange.from?' — '+dateRange.to:''}.`}
              {' '}Запустіть Sync, щоб оновити вибраний період.
            </div>}
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.875rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--color-surface-2)' }}>
                <tr>
                  <th style={{
                    padding: 'var(--space-3) var(--space-3)', textAlign: 'center',
                    borderBottom: '1px solid var(--color-border)', width: 40
                  }}>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          const ids = report.map(r => getActionSelectionKey(r)).filter((id): id is string => !!id);
                          setSelectedIds(new Set(ids));
                        } else {
                          setSelectedIds(new Set());
                        }
                      }}
                      checked={report.length > 0 && selectedIds.size > 0 && selectedIds.size === report.filter(r => getActionSelectionKey(r)).length}
                      disabled={report.every(r => !getActionSelectionKey(r))}
                      title={activeTab === 'geo' || activeTab === 'accounts' || activeTab === 'creative' ? 'Дії недоступні для цього звіту' : undefined}
                    />
                  </th>
                  {visibleColumns.map((col) => {
                    const isCurrencyCol = ['spend', 'cpm', 'cpc', 'cost_per_lead', 'cost_per_registration', 'cost_per_purchase', 'cost_per_app_install'].includes(col.key);
                    return (
                      <th
                        key={col.key}
                        onClick={() => col.key !== 'budget' && toggleSort(col.field)}
                        style={{
                          padding: 'var(--space-3) var(--space-3)',
                          textAlign: col.align,
                          fontWeight: 600, fontSize: '0.72rem',
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          color: sortConfig.field === col.field ? 'var(--color-primary)' : 'var(--color-text-muted)',
                          borderBottom: '1px solid var(--color-border)',
                          cursor: col.key !== 'budget' ? 'pointer' : 'default',
                          whiteSpace: 'nowrap', userSelect: 'none',
                          transition: 'color var(--transition-fast)',
                        }}
                      >
                        {col.label} {isCurrencyCol && currencySymbol ? `(${currencySymbol})` : ''}
                        {col.key !== 'budget' && <SortArrow active={sortConfig.field === col.field} direction={sortConfig.direction} />}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {report.slice(0, visibleRowCount).map((row, idx) => (
                  <tr
                    key={row.rowKey}
                    className={(isSelected(getRowKey(row)) ? 'data-row data-row--selected' : 'data-row')+(row.automation?.highlight?' trigger-automation-row':'')}
                    style={automationStyle(row.automation)}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button, a, input, summary, details')) return;
                      toggleSelection(getRowKey(row));
                    }}
                  >
                    <td style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'center', verticalAlign: 'middle' }}>
                      {(() => {
                        const fbId = getActionSelectionKey(row);
                        return (
                          <input
                            type="checkbox"
                            checked={fbId ? selectedIds.has(fbId) : false}
                            disabled={!fbId}
                            aria-label={'Вибрати '+(row.ad_name??row.campaign_name??row.rowKey)}
                            title={!fbId ? 'Для цього рядка призначення недоступне' : row.rowKind==='creative'?'Вибрати крео для призначення тригера':undefined}
                            onChange={(e) => {
                              if (!fbId) return;
                              if (e.nativeEvent instanceof MouseEvent && e.nativeEvent.shiftKey && lastCheckedIndexRef.current !== null) {
                                // Range select
                                const from = Math.min(lastCheckedIndexRef.current, idx);
                                const to = Math.max(lastCheckedIndexRef.current, idx);
                                const next = new Set(selectedIds);
                                const shouldSelect = e.target.checked;
                                for (let i = from; i <= to; i++) {
                                  const rid = getActionSelectionKey(report[i]);
                                  if (rid) { if (shouldSelect) next.add(rid); else next.delete(rid); }
                                }
                                setSelectedIds(next);
                              } else {
                                const next = new Set(selectedIds);
                                if (e.target.checked) next.add(fbId);
                                else next.delete(fbId);
                                setSelectedIds(next);
                              }
                              lastCheckedIndexRef.current = idx;
                            }}
                          />
                        );
                      })()}
                    </td>
                    {visibleColumns.map((col) => {
                      if (col.key === 'budget') {
                        let displayVal = '—';
                        let canEdit = false;
                        let editType: 'daily' | 'lifetime' = 'daily';
                        let currentBudget = 0;
                        let editId: string | null = row.ad_id;
                        let editMatchField: 'campaign_id' | 'adset_id' = 'adset_id';

                        if (activeTab === 'campaigns') {
                          editId = row.campaign_id ?? null;
                          editMatchField = 'campaign_id';
                          if (row.campaign_budget && editId) {
                            displayVal = fmtMoney(row.campaign_budget);
                            canEdit = true;
                            editType = row.campaign_budget_type as 'daily' | 'lifetime';
                            currentBudget = row.campaign_budget;
                          } else if (row.campaign_budget && !editId) {
                            displayVal = fmtMoney(row.campaign_budget);
                            canEdit = false;
                          } else {
                            displayVal = 'ABO';
                          }
                        } else if (activeTab === 'ads') {
                          if (row.campaign_budget) {
                            displayVal = fmtMoney(row.campaign_budget);
                            currentBudget = row.campaign_budget;
                            editId = row.campaign_id ?? null;
                            editMatchField = 'campaign_id';
                            editType = row.campaign_budget_type as 'daily' | 'lifetime';
                            canEdit = !!editId;
                          } else if (row.adset_budget) {
                            displayVal = fmtMoney(row.adset_budget);
                            editType = row.adset_budget_type as 'daily' | 'lifetime';
                            currentBudget = row.adset_budget;
                            editMatchField = 'adset_id';
                            if (row.adset_id) {
                              editId = row.adset_id;
                              canEdit = true;
                            } else {
                              editId = null;
                              canEdit = false;
                            }
                          } else {
                            displayVal = '—';
                          }
                        }

                        let preview: ReturnType<typeof quoteBudget> | null = null;
                        const enteredBudget = Number(budgetInputValue);
                        try {
                          if (Number.isSafeInteger(enteredBudget)) preview = quoteBudget(enteredBudget, Number(row.budget_usd_rate), Number(row.budget_offset));
                        } catch { /* Invalid input disables saving. */ }
                        return (
                          <td key={col.key} style={{ padding: 'var(--space-2) var(--space-3)', textAlign: col.align, verticalAlign: 'middle', position: 'relative' }} className="budget-cell">
                            {editId && editingBudgetRowId === editId ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px', justifyContent: 'flex-end', minWidth: 230 }}>
                                <span className="text-xs">USD</span>
                                <input
                                  type="number"
                                  aria-label="Бюджет у USD"
                                  min="0"
                                  step="1"
                                  disabled={budgetSaving}
                                  value={budgetInputValue}
                                  onChange={(e) => setBudgetInputValue(e.target.value)}
                                  className="filter-input"
                                  style={{ width: 80, padding: '2px 4px', fontSize: '0.8125rem' }}
                                  autoFocus
                                />
                                <button disabled={budgetSaving || !preview} className="btn btn--ghost" style={{ padding: '2px', color: 'var(--color-success)' }} onClick={() => editId && handleBudgetSave(editId, editType, editMatchField,row.ad_account_id)}>✓</button>
                                <button className="btn btn--ghost" style={{ padding: '2px', color: 'var(--color-danger)' }} disabled={budgetSaving} onClick={() => setEditingBudgetRowId(null)}>✕</button>
                                <small role="status" style={{ flexBasis: '100%', whiteSpace: 'normal' }}>
                                  {editMatchField === 'campaign_id' && <>Бюджет усієї кампанії<br /></>}
                                  {preview ? <>{preview.nativeAmount.toLocaleString('uk-UA', { maximumFractionDigits: 2 })} {row.budget_currency}</> : 'Введіть допустимий цілий бюджет у USD'}
                                </small>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                                <span
                                  style={{ fontVariantNumeric: 'tabular-nums', fontWeight: canEdit ? 600 : 400 }}
                                  title={!canEdit && !editId && displayVal !== 'ABO' && displayVal !== '—' ? 'Немає справжнього campaign_id від Facebook — редагування недоступне' : undefined}
                                >
                                  {displayVal}{currentBudget > 0 ? ' USD' : ''}
                                  {activeTab === 'ads' && row.campaign_budget ? ' (CBO)' : ''}
                                </span>
                                {canEdit && editId && (
                                  <button
                                    className="btn btn--ghost edit-btn"
                                    disabled={budgetSaving}
                                    style={{ padding: '2px', opacity: 0.5 }}
                                    onClick={() => {
                                      setEditingBudgetRowId(editId);
                                      setBudgetInputValue(String(Math.round(currentBudget)));
                                    }}
                                  >
                                    ✎
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      }

                      return (
                        <td
                          key={col.key}
                          style={{ padding: 'var(--space-2) var(--space-3)', textAlign: col.align, verticalAlign: 'middle' }}
                        >
                          {activeTab === 'creative' && col.key === 'ad_name' ? (
                            <button
                              className="btn btn--ghost"
                              style={{ padding: 0, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3 }}
                              onClick={() => row.creative && openCreativeCampaigns(row.creative)}
                              title="Open matching campaigns"
                            >
                              <span>{row.ad_name}</span>
                              <small style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>Active: {row.campaign_counts?.active ?? 0} · Paused: {row.campaign_counts?.paused ?? 0}{row.campaign_counts?.rejected?` · Rejected: ${row.campaign_counts.rejected}`:''}{row.campaign_counts?.scheduled?` · Scheduled: ${row.campaign_counts.scheduled}`:''}{row.campaign_counts?.preparing?` · Preparing: ${row.campaign_counts.preparing}`:''}{row.campaign_counts?.notDelivering?` · Not delivering: ${row.campaign_counts.notDelivering}`:''}{row.campaign_counts?.banned?` · Policy: ${row.campaign_counts.banned}`:''}</small>
                            </button>
                          ) : col.render(row, dateRange)}
                          {activeTab === 'creative' && col.key === 'ad_name' && <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>{row.campaignBadges?.map(badge=><span key={badge} className="badge badge--currency" title="Бейдж із початку назви кампанії">{badge}</span>)}{row.socialAccounts?.map(account=><SocialBadge key={account.id} {...account}/>)}<GeoBadges codes={row.geoCountries}/></div>}
                          {col.key===(activeTab==='campaigns'?'campaign_name':'ad_name')&&<TriggerBadge automation={row.automation} onDetails={setTriggerExecutionId}/>}

                        </td>
                      );
                    })}
                  </tr>
                ))}
                {visibleRowCount < report.length && <tr ref={reportSentinel} className="report-load-more"><td colSpan={visibleCols.size + 1} style={{textAlign:'center',padding:8}}>
                  <button className="btn btn--ghost btn--sm" onClick={()=>setVisibleRowCount(count=>Math.min(count+100,report.length))}>+100</button>
                </td></tr>}
              </tbody>

              <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 10, background: 'var(--color-surface-2)' }}>
                <tr style={{ fontWeight: 700 }}>
                  <td style={{ borderTop: '2px solid var(--color-border)' }}></td>
                  {visibleColumns.map((col) => (
                    <td
                      key={col.key}
                      style={{ padding: 'var(--space-3) var(--space-3)', textAlign: col.align, verticalAlign: 'middle', borderTop: '2px solid var(--color-border)' }}
                    >
                      {totals[col.key]}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── Account filter modal ──────────────────────────── */}
      <AccountFilterModal
        isOpen={filterModalOpen}
        currentFilter={accountFilter}
        onApply={setAccountFilter}
        onClose={() => setFilterModalOpen(false)}
      />

      <CopyForSheetsModal
        isOpen={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        report={report}
      />
    </div>
  );
}
